'use client';

/**
 * Host "Connect a folder" dialog behind `host.local.pick()` — the consent
 * chrome for the `local.*` bridge family (a folder on the viewer's own
 * computer via the File System Access API).
 *
 * Why a host dialog at all: `showDirectoryPicker()` requires transient user
 * activation, and a sandboxed widget iframe's click does NOT carry activation
 * across postMessage to the apex. So the widget requests, WE render a prompt
 * over the canvas, and the user's click on OUR "Choose folder" button is the
 * fresh host-side gesture that lets the native picker open. The native OS
 * picker (folder selection + read/write grant) is the real security boundary;
 * this dialog just explains it and supplies the gesture.
 *
 * Opened via `CustomEvent('widget-local-pick')` with `{ widgetName, resolve }`.
 * Resolves the chosen `FileSystemDirectoryHandle` (read/write granted) or null.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useT } from '@/components/i18n-provider';
import { HardDrive, X } from 'lucide-react';
import { useAppFrameFocusReturn } from '@/components/workspace/app-frame-focus-return';

interface PickRequest {
  widgetName: string;
  resolve: (handle: FileSystemDirectoryHandle | null) => void;
}

export function WidgetLocalFolderDialog() {
  const t = useT();
  const [req, setReq] = useState<PickRequest | null>(null);
  // Close hands focus back to the app that asked (app-frame-focus-return.ts).
  const captureAppFocus = useAppFrameFocusReturn(!!req);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const settledRef = useRef(false);

  useEffect(() => {
    const onOpen = (e: Event) => {
      const detail = (e as CustomEvent<PickRequest>).detail;
      if (!detail || typeof detail.resolve !== 'function') return;
      captureAppFocus();
      settledRef.current = false;
      setError(null);
      setBusy(false);
      setReq(detail);
    };
    window.addEventListener('widget-local-pick', onOpen);
    return () => window.removeEventListener('widget-local-pick', onOpen);
  }, [captureAppFocus]);

  const settle = useCallback(
    (handle: FileSystemDirectoryHandle | null) => {
      if (settledRef.current) return;
      settledRef.current = true;
      req?.resolve(handle);
      setReq(null);
    },
    [req],
  );

  const choose = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const picker = (window as unknown as {
        showDirectoryPicker?: (opts?: { mode?: 'read' | 'readwrite' }) => Promise<FileSystemDirectoryHandle>;
      }).showDirectoryPicker;
      if (!picker) throw new Error('unsupported');
      // Request read/write up front so transfers INTO the folder work without
      // a second prompt. The browser still confines access to this one tree.
      const handle = await picker({ mode: 'readwrite' });
      const withPerm = handle as FileSystemDirectoryHandle & {
        requestPermission?: (o: { mode: 'read' | 'readwrite' }) => Promise<PermissionState>;
      };
      if (typeof withPerm.requestPermission === 'function') {
        const state = await withPerm.requestPermission({ mode: 'readwrite' });
        if (state !== 'granted') {
          // Read-only is still useful (browse + copy OUT); keep the handle.
        }
      }
      settle(handle);
    } catch (err) {
      // AbortError = the user dismissed the native picker; just close quietly.
      if (err instanceof DOMException && err.name === 'AbortError') {
        settle(null);
        return;
      }
      setBusy(false);
      setError('Could not open the folder picker. This needs Chrome or Edge on desktop.');
    }
  }, [settle]);

  useEffect(() => {
    if (!req) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) settle(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [req, busy, settle]);

  if (!req) return null;

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-black/40 backdrop-blur-[2px]"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy) settle(null);
      }}
    >
      <div className="w-[440px] max-w-[calc(100vw-32px)] rounded-2xl border border-border bg-background p-5 shadow-2xl">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <HardDrive className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-sm font-semibold leading-snug">
              {t('workspace.localFolder.title')}
            </h3>
            <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
              “{req.widgetName}” will be able to browse and change files in the
              one folder you choose next, and nothing outside it. The files stay
              on your computer. Access lasts until you reload.
            </p>
          </div>
          <button
            type="button"
            aria-label={t('actions.cancel')}
            onClick={() => !busy && settle(null)}
            className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {error && (
          <p className="mt-3 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-[11.5px] text-destructive">
            {error}
          </p>
        )}

        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={() => settle(null)}
            disabled={busy}
            className="rounded-lg px-3 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-50"
          >
            {t('actions.cancel')}
          </button>
          <button
            type="button"
            onClick={choose}
            disabled={busy}
            className="inline-flex items-center gap-2 rounded-lg bg-primary px-3.5 py-1.5 text-xs font-semibold text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-60"
          >
            <HardDrive className="h-3.5 w-3.5" />
            {busy ? t('workspace.localFolder.opening') : t('workspace.localFolder.chooseFolder')}
          </button>
        </div>
      </div>
    </div>
  );
}
